import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DELAY = 1000;

/**
 * `useDebounce` hook'u, bir fonksiyonun çağrımını belirli bir gecikme süresi boyunca ertelemeye yarar.
 * Bu sayede özellikle input değişimleri veya hızlı tetiklenen eventlerde performans optimizasyonu sağlar.
 *
 * @example
 * const { handleDebounce, cancelDebounce } = useDebounce();
 *
 * // Kullanım: bir input değişiminde API çağrısını debounce etmek
 * const handleChange = (value) => {
 *   handleDebounce({
 *     callback: () => {
 *       console.log('Debounced value:', value);
 *     },
 *     delay: 500, // opsiyonel, default 1000ms
 *   });
 * };
 */
function useDebounce() {
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleDebounce = ({ callback, delay = DEFAULT_DELAY }) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback();
      timeoutRef.current = null;
    }, delay);
  };

  const cancelDebounce = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { handleDebounce, cancelDebounce };
}

export default useDebounce;
